/**
 * CAM-2..CAM-5 (KYCID·00001-00006-00002): restart the PUBLISHED camera track on the requested lens.
 *
 * ── WHY THIS IS A LADDER AND NOT ONE CALL ───────────────────────────────────────────────────────
 * The first version of this file called `restartTrack({ facingMode })` and believed the comment it
 * carried ("restartTrack accepts facingMode directly"). It does — and then quietly makes the request
 * something else. Executed against livekit-client 2.20.0 (see `livekitFacingConstraints.test.ts`,
 * which asserts this against the REAL SDK rather than describing it), `restartTrack({facingMode:
 * 'environment'})` reaches the browser as:
 *
 *     getUserMedia({ audio: false, video: { deviceId: { ideal: 'default' }, facingMode: 'environment' } })
 *
 * Two things are wrong with that request, and together they are why the officer's "use the rear
 * camera" never moved the lens on the owner's handset:
 *
 *  1. `facingMode: 'environment'` written as a bare value is an IDEAL constraint, not a requirement.
 *     getUserMedia is free to hand back the front camera and be entirely spec-compliant; the hint
 *     only nudges its fitness-distance ranking.
 *  2. livekit's own `constraintsForOptions` INJECTS `deviceId: { ideal: 'default' }` whenever the
 *     caller did not name a device. So the browser is asked to prefer the platform-default camera
 *     (the front lens on a handset) and to prefer the rear lens, in the same breath. A request that
 *     argues with itself resolves however the UA feels like resolving it — and on the phones that
 *     matter it resolves to "no change at all".
 *
 * Note what this is NOT: it is not a deviceId left over from an earlier negotiation. `restart()`
 * REPLACES the track's stored constraints, so nothing carries over. The competing deviceId is minted
 * fresh by the SDK on every restart, which is why the failure is perfectly reproducible rather than
 * dependent on what happened earlier in the call.
 *
 * So we stop hinting and start naming, in descending order of how little room the browser has to
 * reinterpret us:
 *
 *   A. `deviceId: { exact: <the target camera> }` — resolved from `enumerateDevices()` via the
 *      device's OWN `getCapabilities().facingMode` (Chrome/Android — the design centre) or its label.
 *      An exact deviceId is the one request a UA cannot reinterpret, and it is the same primitive
 *      livekit's `switchActiveDevice` uses.
 *   B. `facingMode: { exact }` — a REQUIREMENT rather than a hint. Covers iOS Safari, where
 *      `getCapabilities()` is unavailable and labels are localised, so rung A often cannot resolve.
 *   C. `facingMode: <plain>` — what we shipped. Kept as the last rung so no device that used to work
 *      stops working.
 *
 * ── WHY NOT switchActiveDevice ──────────────────────────────────────────────────────────────────
 * livekit-client's `switchActiveDevice(kind, deviceId: string)` takes a DEVICE ID — passing a
 * constraints object there produces `deviceId: {exact: "[object Object]"}`, which matches no device
 * on any hardware (review finding C-9, 2026-08-10: the original call was cast through `as never`, so
 * the type system's refusal was overridden and every switch failed). Rung A above is that finding
 * honoured, not reversed: we give the API a real device id — we just do it through `restartTrack`,
 * which swaps the MediaStreamTrack IN the existing publication, so the recording and the officer's
 * feed continue on the same track with no republish and no renegotiation gap.
 *
 * ── HONESTY CONTRACT ────────────────────────────────────────────────────────────────────────────
 * THREE outcomes, not two, because "the browser will not tell us what it did" is not the same fact
 * as "it did what we asked":
 *
 *   'switched'    — the browser's own report says the live lens IS the requested one (its
 *                   `facingMode`, or the deviceId of a camera it itself described as that lens).
 *   'unverified'  — the restart succeeded and the browser will describe neither the facing nor the
 *                   device. Something changed; we cannot prove WHAT, so we never claim the lens.
 *   'unavailable' — every rung failed, or the browser reports a DIFFERENT lens. A one-camera device
 *                   lands here and that is not an error state (CAM-5): the capture proceeds on
 *                   whatever lens exists and the customer is told plainly.
 *
 * A device with EXACTLY ONE video input never reaches the ladder at all — see the short-circuit in
 * `switchPublishedFacing`. There is no second lens to move to, so attempting one would blink the
 * viewfinder three times, pass through the dead-camera window below, and — on a browser that will
 * not describe its own track — end on 'unverified', telling the customer their camera changed when
 * nothing could have.
 *
 * The previous contract collapsed 'unverified' into success ("reported === undefined || reported ===
 * facing"), which is precisely how a switch that never happened could present as one that did.
 *
 * ── AND IT PUTS THE CAMERA BACK ─────────────────────────────────────────────────────────────────
 * livekit's `restart()` STOPS the existing MediaStreamTrack BEFORE it calls getUserMedia. A refused
 * constraint therefore leaves the publication holding a dead camera — the officer's feed and the
 * recording go black mid-call, which on a V-CIP call is an evidence gap, not a UI glitch. Every rung
 * that throws is followed by a bare `restartTrack()` (no options → livekit re-acquires on the last
 * constraints that WORKED, because it only stores them after a successful acquisition).
 */
export type CameraFacing = 'user' | 'environment';

/** What we can honestly say happened. See the honesty contract above. */
export type FacingSwitchResult = 'switched' | 'unverified' | 'unavailable';

export interface FacingSwitchOutcome {
  readonly result: FacingSwitchResult;
  /**
   * The lens we can honestly say is live now, or null when nothing we can read says. Drives the
   * preview mirroring: a rear-camera document rendered mirrored is unreadable, and guessing here
   * would mirror the wrong one.
   */
  readonly observed: CameraFacing | null;
  /**
   * True when the device PROVABLY has exactly one camera (enumeration listed a single video
   * input), so an 'unavailable' is a fact about the hardware, not a failure of the attempt. The
   * overlay words the two differently — "this device has one camera" vs "we couldn't switch" —
   * because telling a laptop user their camera switch FAILED overstates what happened (owner round
   * 2026-08-29, I2). Omitted everywhere else: absence means "we cannot say", never "more than one
   * lens exists".
   */
  readonly singleCamera?: boolean;
}

/**
 * The slice of LocalVideoTrack this needs — structural, so tests can hand in an honest stub.
 *
 * `facingMode` is typed wider than livekit's `VideoCaptureOptions` (which declares the plain union
 * only). That is deliberate and load-bearing: the platform's own type for the constraint is
 * `ConstrainDOMString`, livekit copies the caller's value through verbatim, and rung B needs the
 * `{exact}` form to reach getUserMedia. This is a WIDENING backed by an executed assertion against
 * the real SDK — not the C-9 pattern of casting a wrong shape through `as never` to silence a
 * signature that was right to refuse it.
 */
export interface RestartableVideoTrack {
  restartTrack(options?: {
    facingMode?: CameraFacing | ConstrainDOMString;
    deviceId?: ConstrainDOMString;
  }): Promise<void>;
  mediaStreamTrack?: MediaStreamTrack | undefined;
}

/** Injected in tests; in the browser it is `navigator.mediaDevices.enumerateDevices`. */
export type DeviceEnumerator = () => Promise<MediaDeviceInfo[]>;

const OPPOSITE: Record<CameraFacing, CameraFacing> = {
  user: 'environment',
  environment: 'user',
};

/**
 * Label shapes real browsers actually produce, post-permission (labels are empty strings until the
 * customer has granted the camera — by the time an officer asks for a lens, they have):
 *   Android Chrome — "camera2 0, facing back" / "camera2 1, facing front"
 *   iOS Safari     — "Back Camera" / "Front Camera" / "Back Dual Wide Camera"
 *   desktop        — vendor names with no facing word at all (correctly matches nothing)
 * Deliberately conservative: a label that matches BOTH families, or matches nothing, resolves to no
 * device rather than to a guess. Picking the wrong lens is worse than not switching — it would
 * publish the wrong camera into the record while reporting success.
 */
const LABEL_HINT: Record<CameraFacing, RegExp> = {
  environment: /\b(back|rear|environment)\b/i,
  user: /\b(front|user|selfie)\b/i,
};

function capabilityFacings(device: MediaDeviceInfo): readonly string[] {
  try {
    const caps = (device as Partial<InputDeviceInfo>).getCapabilities?.();
    const facing = (caps as MediaTrackCapabilities | undefined)?.facingMode;
    return Array.isArray(facing) ? facing : [];
  } catch {
    // Firefox and some webviews expose the method and throw from it. An absent capability is an
    // absent answer, never a reason to fail the switch.
    return [];
  }
}

function browserEnumerator(): Promise<MediaDeviceInfo[]> {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) {
    return Promise.resolve([]);
  }
  return navigator.mediaDevices.enumerateDevices();
}

/**
 * The deviceId of the camera that faces `facing`, or null when nothing PROVABLY is.
 *
 * Enumeration only — never getUserMedia. A second acquisition can steal the device on iOS Safari and
 * black out the officer's feed, which is the whole reason the overlay asks the room owner to switch
 * rather than opening its own camera.
 */
export async function resolveCameraDeviceId(
  facing: CameraFacing,
  enumerate: DeviceEnumerator = browserEnumerator,
): Promise<string | null> {
  return (await surveyCameras(facing, enumerate)).targetDeviceId;
}

/** What enumeration could tell us about this device's cameras. */
export interface CameraSurvey {
  /**
   * How many video inputs the browser LISTS. Zero is not "no cameras" — it is "enumeration told us
   * nothing" (unavailable, refused, or a runtime without mediaDevices), and must never be read as a
   * fact about the hardware.
   */
  readonly cameraCount: number;
  /** The deviceId of the camera that faces `facing`, or null when nothing PROVABLY is. */
  readonly targetDeviceId: string | null;
}

/**
 * Enumerate ONCE and answer both questions the switch needs: how many lenses exist, and which one is
 * the target. Two questions from one enumeration, because asking twice invites the two answers to
 * disagree about the same device list.
 */
export async function surveyCameras(
  facing: CameraFacing,
  enumerate: DeviceEnumerator = browserEnumerator,
): Promise<CameraSurvey> {
  let devices: MediaDeviceInfo[];
  try {
    devices = await enumerate();
  } catch {
    return { cameraCount: 0, targetDeviceId: null };
  }
  const cameras = devices.filter(
    (d) => d.kind === 'videoinput' && typeof d.deviceId === 'string' && d.deviceId !== '',
  );
  const cameraCount = cameras.length;
  // One camera is not a failure and not a switch: there is nothing to move to (CAM-5).
  if (cameraCount < 2) return { cameraCount, targetDeviceId: null };

  // 1. The device's own capabilities — the browser describing its own hardware.
  const byCapability = cameras.filter((d) => capabilityFacings(d).includes(facing));
  if (byCapability.length > 0) {
    // Modern handsets expose several rear lenses (wide / ultra-wide / tele). Any of them IS the
    // requested facing, so the first is a proven member of the right group, never a cross-group guess.
    return { cameraCount, targetDeviceId: byCapability[0]!.deviceId };
  }

  // 2. The label, conservatively.
  const byLabel = cameras.filter(
    (d) =>
      typeof d.label === 'string'
      && LABEL_HINT[facing].test(d.label)
      && !LABEL_HINT[OPPOSITE[facing]].test(d.label),
  );
  if (byLabel.length > 0) return { cameraCount, targetDeviceId: byLabel[0]!.deviceId };

  // Nothing here identifies a lens. Say so — the facingMode rungs still get their turn.
  return { cameraCount, targetDeviceId: null };
}

function normaliseFacing(reported: unknown): CameraFacing | null {
  return reported === 'user' || reported === 'environment' ? reported : null;
}

function readSettings(track: RestartableVideoTrack): MediaTrackSettings | null {
  try {
    return track.mediaStreamTrack?.getSettings?.() ?? null;
  } catch {
    return null;
  }
}

/**
 * What the browser says about the track we just restarted. Client-side truth only — a virtual camera
 * can lie, and provenance is the attested-capture roadmap, not this — but it stops US claiming a
 * switch the browser itself says did not happen.
 */
function verify(
  track: RestartableVideoTrack,
  facing: CameraFacing,
  expectedDeviceId: string | null,
): FacingSwitchOutcome {
  const settings = readSettings(track);
  const reported = normaliseFacing(settings?.facingMode);
  if (reported) {
    // The most specific statement available, and it wins even over a deviceId we chose ourselves.
    return { result: reported === facing ? 'switched' : 'unavailable', observed: reported };
  }
  const liveId = settings?.deviceId;
  if (expectedDeviceId && typeof liveId === 'string' && liveId !== '') {
    return liveId === expectedDeviceId
      ? { result: 'switched', observed: facing }
      : { result: 'unavailable', observed: null };
  }
  return { result: 'unverified', observed: null };
}

async function recoverPreviousCapture(track: RestartableVideoTrack): Promise<void> {
  try {
    // No options: livekit re-acquires on its stored constraints, which a FAILED restart never
    // overwrote — so this is the last capture that actually worked, not a fresh guess.
    await track.restartTrack();
  } catch {
    // Nothing further we can honestly do here. The publication's own recovery ladder (#12, the
    // camera-lost path in useLiveCall) owns the next attempt; inventing another one here would race it.
  }
}

async function attemptRung(
  track: RestartableVideoTrack,
  options: Parameters<RestartableVideoTrack['restartTrack']>[0],
  facing: CameraFacing,
  expectedDeviceId: string | null,
): Promise<FacingSwitchOutcome> {
  try {
    await track.restartTrack(options);
  } catch {
    await recoverPreviousCapture(track);
    return { result: 'unavailable', observed: normaliseFacing(readSettings(track)?.facingMode) };
  }
  return verify(track, facing, expectedDeviceId);
}

/**
 * Switch the published camera to `facing`. Never throws; never leaves the camera stopped.
 *
 * Stops at the first rung that produces anything other than 'unavailable' — each rung is a real
 * getUserMedia and a visible viewfinder blink, so we spend them only while the previous one has
 * demonstrably failed.
 */
export async function switchPublishedFacing(
  track: RestartableVideoTrack | null | undefined,
  facing: CameraFacing,
  enumerate: DeviceEnumerator = browserEnumerator,
): Promise<FacingSwitchOutcome> {
  if (!track) return { result: 'unavailable', observed: null };

  const { cameraCount, targetDeviceId } = await surveyCameras(facing, enumerate);

  /**
   * EXACTLY ONE VIDEO INPUT — decide here, spend nothing.
   *
   * Review finding (P3, 2026-08-26): this used to fall through the ladder. On a one-camera device
   * `{facingMode:{exact}}` is refused, the recovery restart fires, the plain hint then succeeds
   * (it is only a hint, and there is one camera to give) — and if that browser does not describe
   * its own track, `verify` had nothing to go on and returned 'unverified'. The customer was then
   * told "your camera has changed, but this device does not report which one is now in use" about a
   * device where NOTHING changed and nothing CAN change. Three restarts, three viewfinder blinks,
   * one trip through the dead-camera window, and a false statement at the end of it.
   *
   * By the time an officer asks for a lens the customer has long since granted camera permission,
   * so `enumerateDevices()` is fully populated and a count of 1 is a fact about the hardware, not a
   * permission artefact. (A count of 0 is NOT that fact — it means enumeration told us nothing — so
   * it deliberately falls through to the rungs below.)
   *
   * If that one camera already faces where the officer asked, we are on the requested lens and the
   * honest report is 'switched' — the contract is "the browser's own report says the live lens IS
   * the requested one", which it does; no restart was needed to make it true. Otherwise it is the
   * plain CAM-5 answer: we cannot switch, the current lens keeps working, and the capture proceeds.
   */
  if (cameraCount === 1) {
    const observed = normaliseFacing(readSettings(track)?.facingMode);
    return observed === facing
      ? { result: 'switched', observed, singleCamera: true }
      : { result: 'unavailable', observed, singleCamera: true };
  }

  /**
   * ALREADY ON THE REQUESTED LENS (any camera count) — answer from the browser's own report and
   * spend nothing. The owner-round auto-apply (2026-08-29 I2) runs this ladder for every prompt
   * that names a lens — including "front" on a call that is already face-forward — and the CAM-4
   * restore runs it again on close. Without this check each of those would burn a full restart (a
   * viewfinder blink and a trip through livekit's stop-then-acquire window) to arrive exactly where
   * we already are. 'switched' is honest by the same contract as the one-camera case above: the
   * browser's own report says the live lens IS the requested one. A track whose settings name no
   * facing falls through to the rungs — we never assume, we ask.
   */
  const alreadyLive = normaliseFacing(readSettings(track)?.facingMode);
  if (alreadyLive === facing) return { result: 'switched', observed: alreadyLive };

  if (targetDeviceId) {
    const a = await attemptRung(track, { deviceId: { exact: targetDeviceId } }, facing, targetDeviceId);
    if (a.result !== 'unavailable') return a;
  }

  const b = await attemptRung(track, { facingMode: { exact: facing } }, facing, null);
  if (b.result !== 'unavailable') return b;

  return attemptRung(track, { facingMode: facing }, facing, null);
}
